<template>
  <div class="pc-sidebar" :class="{ 'pc-sidebar-hide': isSidebarCollapsed }">
    <!-- Brand stripe: the four VESPROC logo colours, per the design system. -->
    <div class="pc-sidebar-brandbar" aria-hidden="true">
      <i></i><i></i><i></i><i></i>
    </div>
    <div class="pc-sidebar-header">
      <a href="#" class="pc-sidebar-logo">
        <img src="@/assets/images/logo/vesproc.svg" :alt="$t('common.navigation.logoAlt')" class="logo-lg" />
      </a>
      <!--
        Collapse lives next to the logo, as in the design. It goes with the sidebar when
        it closes, so the header keeps its own toggle as the way back in.
      -->
      <button
        type="button"
        class="pc-sidebar-toggle"
        :title="$t('common.navigation.toggleMenu')"
        :aria-label="$t('common.navigation.toggleMenu')"
        @click="$emit('toggle-sidebar')"
      >
        <i class="ph-duotone ph-list"></i>
      </button>
    </div>
    <div class="pc-sidebar-content">
      <ul class="pc-navbar">
        <!--
          An unapproved supplier gets one link, not an empty sidebar. The router refuses
          every other path anyway; showing the menu would only offer doors that bounce.
        -->
        <template v-if="confinedToOnboarding">
          <li class="pc-item pc-caption">
            <label>{{ $t('onboarding.title') }}</label>
          </li>
          <li class="pc-item">
            <router-link
              class="pc-link"
              :class="{ 'active': $route.path.startsWith('/onboarding') }"
              to="/onboarding"
              :title="$t('onboarding.title')"
            >
              <span class="pc-micon"><i class="ph-duotone ph-clipboard-text"></i></span>
              <span class="pc-mtext">{{ $t('onboarding.title') }}</span>
            </router-link>
          </li>
        </template>

        <!-- Standard menu items (hidden for buyer tenants) -->
        <!-- Only once confinement is known: until then an unapproved supplier would see the
             whole menu for a moment on every reload. -->
        <template v-if="!isBuyerTenant && confinedToOnboarding === false">
          
          <!-- Procurement section -->
          <template v-if="canAccessRfq() || canAccessPurchaseOrders()">
            <li class="pc-item pc-caption">
              <label>{{ $t('common.navigation.procurement') }}</label>
            </li>
            
            <!-- RFQ menu (only for users with quotation permissions) -->
            <li v-if="canAccessRfq() "  class="pc-item">
              <router-link class="pc-link" :class="{ 'active': $route.path === '/rfq' }" to="/rfq" :title="$t('common.navigation.rfq')">
                <span class="pc-micon">
                  <i class="ph-duotone ph-file-text"></i>
                </span>
                <span class="pc-mtext">{{ $t('common.navigation.rfq') }}</span>
              </router-link>
            </li>
            
            <!-- Purchase Order menu (only for users with purchase order permissions) -->
            <li v-if="canAccessPurchaseOrders()" class="pc-item">
              <router-link class="pc-link" :class="{ 'active': $route.path === '/purchase-order' }" to="/purchase-order" :title="$t('common.navigation.purchaseOrder')">
                <span class="pc-micon">
                  <i class="ph-duotone ph-shopping-cart"></i>
                </span>
                <span class="pc-mtext">{{ $t('common.navigation.purchaseOrder') }}</span>
              </router-link>
            </li>
          </template>
          
          <!-- Administration: one collapsible group per admin area -->
          <template v-if="showAdminSection">
            <li class="pc-item pc-caption">
              <label>{{ $t('common.navigation.administration') }}</label>
            </li>

            <li
              v-for="group in visibleGroups"
              :key="group.key"
              class="pc-item"
              :class="{ 'pc-hasmenu': group.items.length > 1, 'pc-trigger': isGroupOpen(group) }"
            >
              <!-- Single-item groups link straight to the screen; no pointless submenu -->
              <router-link
                v-if="group.items.length === 1"
                class="pc-link"
                :class="{ 'active': isItemActive(group.items[0]) }"
                :to="group.items[0].path"
                :title="$t(group.labelKey)"
              >
                <span class="pc-micon"><i :class="group.icon"></i></span>
                <span class="pc-mtext">{{ $t(group.labelKey) }}</span>
              </router-link>

              <template v-else>
                <a href="#" class="pc-link" @click.prevent="toggleGroup(group.key)" :title="$t(group.labelKey)">
                  <span class="pc-micon"><i :class="group.icon"></i></span>
                  <span class="pc-mtext">{{ $t(group.labelKey) }}</span>
                  <span class="pc-arrow"><i class="ti ti-chevron-right"></i></span>
                </a>
                <!-- Visibility comes from .pc-trigger on the parent (theme CSS) -->
                <ul class="pc-submenu">
                  <li v-for="item in group.items" :key="item.key" class="pc-item">
                    <router-link
                      class="pc-link"
                      :class="{ 'active': isItemActive(item) }"
                      :to="item.path"
                    >
                      <span class="pc-mtext">{{ $t(item.labelKey) }}</span>
                    </router-link>
                  </li>
                </ul>
              </template>
            </li>

            <!-- Databank (MaterialCategory or PrintTemplate permission) -->
            <li v-if="canAccessDatabank" class="pc-item">
              <router-link class="pc-link" :class="{ 'active': $route.path.startsWith('/databank') }" to="/databank" :title="$t('common.navigation.databank')">
                <span class="pc-micon">
                  <i class="ph-duotone ph-database"></i>
                </span>
                <span class="pc-mtext">{{ $t('common.navigation.databank') }}</span>
              </router-link>
            </li>
          </template>

        </template>
        
        <!-- LookUp menu (only for buyer tenants) -->
        <li v-if="isBuyerTenant" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path === '/lookup' }" to="/lookup" :title="$t('common.navigation.lookup')">
            <span class="pc-micon">
              <i class="ph-duotone ph-magnifying-glass"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.lookup') }}</span>
          </router-link>
        </li>
        
        <!-- Mail Tracking menu (buyer tenants + MailTracking permission when not impersonating) -->
        <li v-if="isBuyerTenant || (canAccessMailTracking && !currentUser?.isImpersonating)" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path.startsWith('/mail-tracking') }" to="/mail-tracking" :title="$t('common.navigation.mailTracking')">
            <span class="pc-micon">
              <i class="ph-duotone ph-envelope-open"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.mailTracking') }}</span>
          </router-link>
        </li>
        
        <!-- Quotation Mail Recipients menu (only for buyer tenants) -->
        <li v-if="isBuyerTenant" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path === '/quotation-mail-recipients' }" to="/quotation-mail-recipients" :title="$t('common.navigation.quotationMailRecipients')">
            <span class="pc-micon">
              <i class="ph-duotone ph-envelope-simple"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.quotationMailRecipients') }}</span>
          </router-link>
        </li>

        <!-- Tenant Review menu (only for buyer tenants) -->
        <li v-if="isBuyerTenant" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path.startsWith('/tenant-review') }" to="/tenant-review" :title="$t('common.navigation.tenantReview')">
            <span class="pc-micon">
              <i class="ph-duotone ph-chat-circle-text"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.tenantReview') }}</span>
          </router-link>
        </li>
        
        <!-- Account section (hidden for super admin when not impersonating) -->
        <template v-if="shouldShowAccountSection && confinedToOnboarding === false">
          <li class="pc-item pc-caption">
            <label>{{ $t('common.navigation.account') }}</label>
          </li>
          
          <!--
            Company Profile groups its own sections. They were five sibling entries, which
            buried the rest of the Account menu; collapsing them keeps the profile areas
            together and the sidebar short. Uses the theme's own accordion markup, so
            visibility comes from .pc-trigger in the global CSS.
          -->
          <li
            v-if="!isBuyerTenant"
            class="pc-item pc-hasmenu"
            :class="{ 'pc-trigger': isMenuOpen('account:profile') }"
          >
            <a href="#" class="pc-link" @click.prevent="toggleMenu('account:profile')" :title="$t('common.navigation.profile')">
              <span class="pc-micon">
                <i class="ph-duotone ph-buildings"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.profile') }}</span>
              <span class="pc-arrow"><i class="ti ti-chevron-right"></i></span>
            </a>

            <ul class="pc-submenu">
              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/profile' }" to="/profile">
                  <span class="pc-mtext">{{ $t('common.navigation.profileOverview') }}</span>
                </router-link>
              </li>

              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/profile/categories' }" to="/profile/categories">
                  <span class="pc-mtext">{{ $t('common.navigation.categories') }}</span>
                  <span v-if="sectionCounts" class="pc-count ms-auto">
                    {{ sectionCounts.materialCategoriesCount }}
                  </span>
                </router-link>
              </li>

              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/profile/ports' }" to="/profile/ports">
                  <span class="pc-mtext">{{ $t('common.navigation.ports') }}</span>
                  <span v-if="sectionCounts" class="pc-count ms-auto">
                    {{ sectionCounts.portsCount }}
                  </span>
                </router-link>
              </li>

              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/profile/certificates' }" to="/profile/certificates">
                  <span class="pc-mtext">{{ $t('common.navigation.certificates') }}</span>
                  <span v-if="sectionCounts" class="pc-count ms-auto">
                    {{ sectionCounts.certificatesCount }}
                  </span>
                </router-link>
              </li>

              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/profile/reviews' }" to="/profile/reviews">
                  <span class="pc-mtext">{{ $t('common.navigation.reviews') }}</span>
                  <span v-if="sectionCounts" class="pc-count ms-auto">
                    {{ sectionCounts.reviewsCount }}
                  </span>
                </router-link>
              </li>
            </ul>
          </li>

          <!-- Users menu (only for standard users with permission, not buyer tenants) -->
          <li v-if="!isBuyerTenant && hasAnyPermission(['Supplier.Users', 'Supplier.Administration'])" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path.startsWith('/users') }" to="/users" :title="$t('common.navigation.users')">
              <span class="pc-micon">
                <i class="ph-duotone ph-users"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.users') }}</span>
            </router-link>
          </li>
          
          <!-- Subscription: plan catalogue and the tenant's own membership -->
          <li
            v-if="showSubscriptionGroup"
            class="pc-item pc-hasmenu"
            :class="{ 'pc-trigger': isMenuOpen('account:subscription') }"
          >
            <a href="#" class="pc-link" @click.prevent="toggleMenu('account:subscription')" :title="$t('common.navigation.subscription')">
              <span class="pc-micon"><i class="ph-duotone ph-credit-card"></i></span>
              <span class="pc-mtext">{{ $t('common.navigation.subscription') }}</span>
              <span class="pc-arrow"><i class="ti ti-chevron-right"></i></span>
            </a>
            <ul class="pc-submenu">
              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/subscription/mine' }" to="/subscription/mine">
                  <span class="pc-mtext">{{ $t('subscription.mySubscription.title') }}</span>
                </router-link>
              </li>
              <li class="pc-item">
                <router-link class="pc-link" :class="{ 'active': $route.path === '/subscription/plans' }" to="/subscription/plans">
                  <span class="pc-mtext">{{ $t('subscription.plans') }}</span>
                </router-link>
              </li>
            </ul>
          </li>

          <!--
            Only buyer tenants have a second settings screen. For everyone else the group
            held a single item, so it goes straight to that screen instead of making you
            open a submenu to reach the one thing inside it.
          -->
          <li
            class="pc-item"
            :class="{ 'pc-hasmenu': isBuyerTenant, 'pc-trigger': isBuyerTenant && isMenuOpen('account:settings') }"
          >
            <router-link
              v-if="!isBuyerTenant"
              class="pc-link"
              :class="{ 'active': $route.path.startsWith('/settings') }"
              to="/settings/profile"
              :title="$t('common.navigation.settings')"
            >
              <span class="pc-micon"><i class="ph-duotone ph-gear-six"></i></span>
              <span class="pc-mtext">{{ $t('common.navigation.settings') }}</span>
            </router-link>

            <template v-else>
              <a href="#" class="pc-link" @click.prevent="toggleMenu('account:settings')" :title="$t('common.navigation.settings')">
                <span class="pc-micon"><i class="ph-duotone ph-gear-six"></i></span>
                <span class="pc-mtext">{{ $t('common.navigation.settings') }}</span>
                <span class="pc-arrow"><i class="ti ti-chevron-right"></i></span>
              </a>
              <ul class="pc-submenu">
                <li class="pc-item">
                  <router-link class="pc-link" :class="{ 'active': $route.path === '/settings/profile' }" to="/settings/profile">
                    <span class="pc-mtext">{{ $t('settings.account') }}</span>
                  </router-link>
                </li>
                <li class="pc-item">
                  <router-link class="pc-link" :class="{ 'active': $route.path === '/settings/quotation-expiry' }" to="/settings/quotation-expiry">
                    <span class="pc-mtext">{{ $t('settings.quotationExpiry.title') }}</span>
                  </router-link>
                </li>
              </ul>
            </template>
          </li>
          
          <!-- Contracts the tenant has accepted (platform switch: off until released) -->
          <li v-if="!isBuyerTenant && features.contracts" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/contracts' }" to="/contracts" :title="$t('contracts.mine.title')">
              <span class="pc-micon">
                <i class="ph-duotone ph-file-text"></i>
              </span>
              <span class="pc-mtext">{{ $t('contracts.mine.title') }}</span>
            </router-link>
          </li>

          <!-- Help menu (visible for lookup users and supplier users, hidden for admin unless impersonating) -->
          <li v-if="shouldShowHelpMenu" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/help' }" to="/help" :title="$t('common.navigation.help')">
              <span class="pc-micon">
                <i class="ph-duotone ph-question"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.help') }}</span>
            </router-link>
          </li>
        </template>
        
        <li class="pc-item">
          <a href="#" class="pc-link" @click.prevent="logout" :title="$t('common.navigation.logout')">
            <span class="pc-micon">
              <i class="ph-duotone ph-sign-out"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.logout') }}</span>
          </a>
        </li>

      </ul>
    </div>

    <!-- Subscription status pinned to bottom of sidebar -->
    <!--
      Bottom panel: profile completion sits above the plan, where it is visible from every
      screen without putting a number next to a menu item. It disappears at 100% — a bar
      that is always full is no longer a prompt to act, just permanent furniture.
    -->
    <div v-if="showCompletionBar" class="sidebar-bottom-panel">
      <button type="button" class="completion-link" @click="showCompletionPanel = true">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="small text-muted">
            <i class="ph-duotone ph-chart-donut me-1"></i>{{ $t('profileCompletion.title') }}
          </span>
          <span class="small fw-semibold" :class="isProfileIncomplete ? 'text-danger' : 'text-success'">
            {{ profilePercentage }}%
          </span>
        </div>

        <div class="progress" style="height: 6px;">
          <div
            class="progress-bar"
            :class="isProfileIncomplete ? 'bg-danger' : 'bg-success'"
            role="progressbar"
            :style="{ width: `${profilePercentage}%` }"
            :aria-valuenow="profilePercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>

        <!-- Only shown when something required is missing, so it stays a real signal. -->
        <div v-if="isProfileIncomplete" class="small text-danger mt-1">
          {{ $t('profileCompletion.incompleteShort') }}
        </div>
      </button>
    </div>

    <ProfileCompletionPanel
      v-if="showCompletionPanel"
      :completion="profileCompletion"
      @close="showCompletionPanel = false"
    />

    <!-- Not during registration: there is no plan to upgrade until the account is approved. -->
    <div v-if="features.subscription && !isBuyerTenant && !currentUser?.isSuperAdmin && confinedToOnboarding === false" class="subscription-widget">
      <div v-if="subscriptionLoading" class="text-center py-1">
        <span class="spinner-border spinner-border-sm text-primary"></span>
      </div>
      <template v-else-if="subscriptionStatus?.isActive">
        <div class="small text-muted mb-1">
          <i class="ph-duotone ph-credit-card me-1"></i>{{ $t('subscription.currentPlan') }}
        </div>
        <div class="fw-semibold small text-truncate">{{ subscriptionStatus.productName }}</div>
        <div class="small text-muted mt-1">
          {{ subscriptionStatus.cancelAtPeriodEnd ? $t('subscription.endsOn') : $t('subscription.renewsOn') }}: {{ $formatDate(subscriptionStatus.endDate) }}
        </div>
      </template>
      <template v-else>
        <div class="small text-muted mb-2">
          <i class="ph-duotone ph-warning me-1"></i>{{ $t('subscription.noActiveSubscription') }}
        </div>
        <router-link to="/subscription/plans" class="btn btn-primary btn-sm w-100">
          <i class="ti ti-bolt me-1"></i>{{ $t('subscription.upgrade') }}
        </router-link>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useRfqPermissions } from '@/composables/useRfqPermissions';
import { usePurchaseOrderPermissions } from '@/composables/usePurchaseOrderPermissions';
import { useOnboardingGate } from '@/composables/useOnboardingGate';
import { useOnboardingDraft } from '@/composables/useOnboardingDraft';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useAdminNav } from '@/composables/useAdminNav';
import { useProfileCompletion } from '@/composables/useProfileCompletion';
import ProfileCompletionPanel from '@/components/profile/ProfileCompletionPanel.vue';
import subscriptionService from '@/services/subscriptionService';
import Swal from 'sweetalert2';

export default {
  name: 'AppSidebar',
  components: { ProfileCompletionPanel },
  props: {
    isSidebarCollapsed: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-sidebar'],
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const route = useRoute();
    const { userPermissions, hasPermission, hasAnyPermission } = usePermissions();
    const { canAccessRfq } = useRfqPermissions();
    const { canAccessPurchaseOrders } = usePurchaseOrderPermissions();

    // Mirrors the router guard. Resolved once on mount rather than per render: it reads
    // the shared draft, which does not change while the supplier is on a page.
    const { isConfinedToOnboarding } = useOnboardingGate();
    // null until resolved, so nothing hidden during registration flashes in first.
    const confinedToOnboarding = ref(null);
    const resolveConfinement = async () => {
      const wasConfined = confinedToOnboarding.value === true;
      confinedToOnboarding.value = await isConfinedToOnboarding();
      // Approved mid-session: the cached score predates the registration data it now counts.
      if (wasConfined && !confinedToOnboarding.value) {
        refreshProfileCompletion();
      }
    };
    const { features } = useFeatureFlags();
    const { visibleGroups, canAccessAdminPanel } = useAdminNav();
    const {
      completion: profileCompletion,
      load: loadProfileCompletion,
      refresh: refreshProfileCompletion,
      isIncomplete: isProfileIncomplete,
      percentage: profilePercentage,
      sectionCounts
    } = useProfileCompletion();

    const currentUser = computed(() => store.getters['auth/currentUser']);

    const settingsRoutes = ['/settings', '/settings/profile', '/settings/quotation-expiry'];

    // Open when the user is somewhere under /profile, so the active item is never
    // hidden inside a collapsed group after a reload or a direct link.
    const showCompletionPanel = ref(false);

    // The bar earns its space only while there is something left to fill. At 100% it
    // goes away rather than sitting there permanently full.
    const showCompletionBar = computed(() =>
      !!profileCompletion.value &&
      !profileCompletion.value.isComplete &&
      !isBuyerTenant.value &&
      !currentUser.value?.isSuperAdmin &&
      // During registration the onboarding navigator is the progress to follow. This bar
      // scores the public profile, whose screens stay closed until the account is approved.
      confinedToOnboarding.value === false
    );
    /*
     * One group open at a time. A group the user explicitly opened wins; until they
     * touch one, the group holding the current route is open so the active item is
     * never buried. Keys are namespaced because an admin group and the Account
     * section can both be called "subscription".
     */
    const manualMenuKey = ref(undefined);

    const groupContainsRoute = (group) => group.items.some(item => route.path.startsWith(item.path));

    const routeMenuKey = computed(() => {
      const group = visibleGroups.value.find(groupContainsRoute);
      if (group) return `admin:${group.key}`;
      if (route.path.startsWith('/profile')) return 'account:profile';
      if (route.path.startsWith('/subscription')) return 'account:subscription';
      if (settingsRoutes.some(r => route.path.startsWith(r))) return 'account:settings';
      return null;
    });

    const openMenuKey = computed(() =>
      manualMenuKey.value === undefined ? routeMenuKey.value : manualMenuKey.value
    );

    const isMenuOpen = (key) => openMenuKey.value === key;

    // Clicking the open group shuts it; clicking another one takes its place.
    const toggleMenu = (key) => {
      manualMenuKey.value = openMenuKey.value === key ? null : key;
    };

    const isGroupOpen = (group) => isMenuOpen(`admin:${group.key}`);
    const toggleGroup = (key) => toggleMenu(`admin:${key}`);

    const isItemActive = (item) => route.path === item.path;

    // Navigation hands control back to the route, so the group holding the new page opens.
    watch(() => route.path, () => {
      manualMenuKey.value = undefined;
    });

    // Impersonating super admins act as the tenant, so admin navigation is hidden.
    const showAdminSection = computed(() =>
      canAccessAdminPanel.value && !currentUser.value?.isImpersonating
    );

    const showSubscriptionGroup = computed(() =>
      features.subscription && !isBuyerTenant.value && !currentUser.value?.isSuperAdmin
    );

    const subscriptionStatus = ref(null);
    const subscriptionLoading = ref(false);

    const loadSubscriptionStatus = async () => {
      if (!features.subscription || isBuyerTenant.value || currentUser.value?.isSuperAdmin) return;
      subscriptionLoading.value = true;
      try {
        subscriptionStatus.value = await subscriptionService.getCurrentStatus();
      } catch {
        // non-critical
      } finally {
        subscriptionLoading.value = false;
      }
    };

    const canAccessDatabank = computed(() =>
      currentUser.value?.isSuperAdmin &&
      !currentUser.value?.isImpersonating &&
      hasPermission('Supplier.SuperAdmin.Databank')
    );

    const canAccessMailTracking = computed(() =>
      currentUser.value?.isSuperAdmin && hasPermission('Supplier.SuperAdmin.MailTracking')
    );

    // Debug logging for super admin detection
    
    
    // Debug logging for permissions
    
    
    // Check if we're in development mode
    const isDevelopment = computed(() => import.meta.env.DEV);
    
    // Check if current user is a buyer tenant based on editionId
    const isBuyerTenant = computed(() => {
      const buyerTenantEditionId = '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0';

      
      // Check both user object and localStorage for editionId
      const userEditionId = currentUser.value?.editionId || localStorage.getItem('editionId');

      
      // Case-insensitive comparison
      const isMatch = userEditionId?.toUpperCase() === buyerTenantEditionId.toUpperCase();

      
      return isMatch;
    });

    onMounted(() => {
      resolveConfinement();
      loadSubscriptionStatus();
      // Buyer tenants have no supplier profile to complete.
      if (!isBuyerTenant.value) {
        loadProfileCompletion();
      }
    });

    // The menu comes back the moment an admin approves the registration, without a reload.
    watch(() => useOnboardingDraft().status.value, resolveConfinement);

    // Reload when the subscription changes elsewhere (activate/cancel/resume)
    watch(() => store.state.subscriptionRefreshKey, () => {
      loadSubscriptionStatus();
    });

    // Check if help menu should be shown
    // Show for lookup users and supplier users, hide for admin unless impersonating
    const shouldShowHelpMenu = computed(() => {
      const user = currentUser.value;
      // Show for all users except super admin when not impersonating
      return !(user?.isSuperAdmin && !user?.isImpersonating);
    });

    // Check if account section should be shown
    // Hide for super admin when not impersonating
    const shouldShowAccountSection = computed(() => {
      const user = currentUser.value;
      
      // Show for non-super admin users
      if (!user?.isSuperAdmin) {
        return true;
      }
      
      // Show for super admin only when impersonating
      return user?.isImpersonating === true;
    });
    
    const logout = async () => {
      try {
        // Show confirmation dialog
        const result = await Swal.fire({
          title: t('common.confirmations.logout.title'),
          text: t('common.confirmations.logout.message'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#6c757d',
          confirmButtonText: t('common.confirmations.logout.confirm'),
          cancelButtonText: t('common.confirmations.logout.cancel'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1'
          },
          buttonsStyling: false
        });
        
        if (!result.isConfirmed) {
          return; // User cancelled logout
        }
        
        await store.dispatch('auth/logout');
        
        // Use window.location.href for a clean redirect that prevents any pending API calls
        window.location.href = '/login';
      } catch (error) {
        console.error('Logout error:', error);
        // Even on error, redirect to login page
        window.location.href = '/login';
      }
    };

    
    return {
      currentUser,
      isBuyerTenant,
      shouldShowHelpMenu,
      shouldShowAccountSection,
      hasPermission,
      hasAnyPermission,
      userPermissions,
      logout,
      isDevelopment,
      canAccessRfq,
      canAccessPurchaseOrders,
      confinedToOnboarding,
      canAccessDatabank,
      canAccessMailTracking,
      subscriptionStatus,
      subscriptionLoading,
      profileCompletion,
      showCompletionBar,
      showCompletionPanel,
      isProfileIncomplete,
      profilePercentage,
      sectionCounts,
      isMenuOpen,
      toggleMenu,
      showSubscriptionGroup,
      visibleGroups,
      showAdminSection,
      isGroupOpen,
      toggleGroup,
      isItemActive,
      features
    };
  }
};
</script>

<style scoped>
/* ============================================================
   Sidebar — VESPROC design-system styling.

   Mirrors the sidebar of the "Admin Tenants" screen in the VESPROC
   design project: brand stripe on top, compact 8px-radius rows with a
   navy edge on the active one, grey-500 captions and icons, and the
   bottom panels framed as light cards. Colours are the --vp-* tokens
   from src/assets/css/vesproc-tokens.css.

   Selectors are rooted at .pc-sidebar on purpose — the theme
   (assets/css/style.css) styles the same markup at that depth, so a
   bare class would lose the specificity match.
   ============================================================ */

.pc-sidebar {
  width: 260px;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: var(--vp-surface);
  box-shadow: var(--vp-shadow-sidebar);
  border-right: 1px solid var(--vp-gray-200);
  z-index: 1029;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

/* The four logo colours, 4px tall — with the header below it the block is
   74px, so its bottom edge lines up with the top header's. */
.pc-sidebar-brandbar {
  display: flex;
  height: 4px;
  flex-shrink: 0;
  overflow: hidden;
}

.pc-sidebar-brandbar i {
  flex: 1;
}

.pc-sidebar-brandbar i:nth-child(1) { background: var(--vp-coral); }
.pc-sidebar-brandbar i:nth-child(2) { background: var(--vp-accent); }
.pc-sidebar-brandbar i:nth-child(3) { background: var(--vp-info); }
.pc-sidebar-brandbar i:nth-child(4) { background: var(--vp-primary); }

.pc-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 20px;
  min-height: 70px;
  border-bottom: 1px solid var(--vp-gray-200);
  background: var(--vp-surface);
  flex-shrink: 0;
}

.pc-sidebar-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0;
}

.pc-sidebar-logo:hover {
  text-decoration: none;
  opacity: 0.85;
}

.logo-lg {
  height: 36px;
  width: auto;
  max-width: 176px;
  object-fit: contain;
  object-position: left;
  display: block;
  margin: 0;
}

.pc-sidebar-toggle {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  background: var(--vp-surface);
  color: var(--vp-body-color);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.pc-sidebar-toggle:hover {
  background: var(--vp-gray-200);
  color: var(--vp-primary);
}

.pc-sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 0;
}

/* A full-width theme scrollbar next to 13px rows reads as a second border. */
.pc-sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.pc-sidebar-content::-webkit-scrollbar-thumb {
  background: var(--vp-gray-300);
  border-radius: 3px;
}

.pc-sidebar .pc-navbar {
  list-style: none;
  margin: 0;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pc-sidebar .pc-navbar > .pc-item {
  width: 100%;
  margin: 0;
}

/* Section captions */
.pc-sidebar .pc-navbar > .pc-caption {
  padding: 12px 10px 4px !important;
  margin: 0;
}

.pc-sidebar .pc-navbar > .pc-caption label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--vp-gray-500);
  margin: 0;
}

/* Rows. The theme paints hover/active through a translucent ::after overlay;
   the design uses a solid tint plus a left edge, so the overlay goes. */
.pc-sidebar .pc-navbar > .pc-item > .pc-link::after {
  display: none;
}

.pc-sidebar .pc-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--vp-radius);
  border-left: 3px solid transparent;
  color: var(--vp-body-color);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.pc-sidebar .pc-link:hover {
  background-color: var(--vp-gray-200);
  color: var(--vp-gray-800);
}

.pc-sidebar .pc-link.active {
  background-color: rgba(var(--vp-primary-rgb), 0.08);
  border-left-color: var(--vp-primary);
  color: var(--vp-primary);
  font-weight: 600;
}

.pc-sidebar .pc-micon {
  margin: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pc-sidebar .pc-micon i {
  font-size: 1.15rem;
  line-height: 1;
  color: var(--vp-gray-500);
  transition: color 0.15s ease;
}

.pc-sidebar .pc-link:hover .pc-micon i {
  color: var(--vp-primary);
}

.pc-sidebar .pc-link.active .pc-micon i {
  color: var(--vp-accent);
}

.pc-sidebar .pc-mtext {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.4;
}

.pc-sidebar .pc-arrow {
  float: none;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--vp-gray-400);
}

/* Submenus: indented under the parent's icon, with the theme's guide line
   pulled to the left of the block and recoloured. */
.pc-sidebar .pc-navbar > .pc-item .pc-submenu {
  margin: 2px 0 2px 24px;
  padding: 0;
}

/* Only the open group lays out. The theme closes the others with
   .pc-hasmenu:not(.pc-trigger) > .pc-submenu, and a display declaration on the plain
   .pc-submenu selector outranks it — which is what kept every group expanded. */
.pc-sidebar .pc-navbar > .pc-item.pc-hasmenu.pc-trigger > .pc-submenu {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pc-sidebar .pc-navbar > .pc-item .pc-submenu::after {
  left: 0;
  border-left-color: var(--vp-gray-200);
}

/* A child row has to read as a child: smaller, lighter and in the muted grey, with its
   text starting where the parent's label does. Size alone left the two levels looking
   like the same line twice. */
.pc-sidebar .pc-navbar > .pc-item .pc-submenu .pc-link {
  padding: 6px 10px 6px 20px;
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--vp-gray-600);
  border-left-width: 0;
}

/* Both of these outrank the generic .pc-link states above, so they restate them. */
.pc-sidebar .pc-navbar > .pc-item .pc-submenu .pc-link:hover {
  color: var(--vp-gray-800);
}

.pc-sidebar .pc-navbar > .pc-item .pc-submenu .pc-link.active {
  color: var(--vp-primary);
  font-weight: 600;
}

/* The theme marks submenu rows with a dot pinned to the old indent. */
.pc-sidebar .pc-navbar > .pc-item .pc-submenu .pc-item > .pc-link::after {
  display: none;
}

/* Count chip on the profile submenu rows. */
.pc-sidebar .pc-count {
  background: var(--vp-light-accent);
  color: var(--vp-gray-800);
  border-radius: var(--vp-radius-chip);
  padding: 1px 7px;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.5;
}

/* Bottom panels, framed as the design's light cards rather than full-width
   strips with a rule above them. */
.sidebar-bottom-panel {
  margin: 8px 12px 0;
  padding: 10px 12px;
  border-radius: var(--vp-radius);
  background: var(--vp-gray-100);
  flex-shrink: 0;
}

/* The whole block is the trigger, so the bar itself is clickable. Rendered as a button
   for keyboard and screen-reader semantics, hence the reset. */
.completion-link {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  padding: 0;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

.completion-link:hover .progress {
  opacity: 0.85;
}

.sidebar-bottom-panel .progress {
  background-color: var(--vp-gray-200);
  border-radius: var(--vp-radius-pill);
}

.subscription-widget {
  margin: 8px 12px 12px;
  padding: 10px 12px;
  border-radius: var(--vp-radius);
  background: var(--vp-gray-100);
  flex-shrink: 0;
}

/* ------------------------------------------------------------
   Collapsed state — a 68px icon rail, as in the design. The bar keeps its icons and
   its toggle; labels, counts, submenus and the bottom panels step aside. The matching
   offsets for the header, content and footer live in assets/css/custom.css.
   ------------------------------------------------------------ */

.pc-sidebar.pc-sidebar-hide {
  width: 68px;
}

.pc-sidebar.pc-sidebar-hide .pc-sidebar-header {
  justify-content: center;
  padding: 12px 10px;
}

.pc-sidebar.pc-sidebar-hide .pc-sidebar-logo {
  display: none;
}

.pc-sidebar.pc-sidebar-hide .pc-navbar {
  padding: 8px 10px;
}

.pc-sidebar.pc-sidebar-hide .pc-link {
  justify-content: center;
  padding: 8px 0;
}

/* The label lives on as the row's title attribute, which is the rail's tooltip. */
.pc-sidebar.pc-sidebar-hide .pc-mtext,
.pc-sidebar.pc-sidebar-hide .pc-arrow,
.pc-sidebar.pc-sidebar-hide .pc-count {
  display: none;
}

/* A caption has no room for its text, so it becomes the rule between groups. */
.pc-sidebar.pc-sidebar-hide .pc-navbar > .pc-caption {
  padding: 8px 6px !important;
}

.pc-sidebar.pc-sidebar-hide .pc-navbar > .pc-caption label {
  display: none;
}

.pc-sidebar.pc-sidebar-hide .pc-navbar > .pc-caption::before {
  content: "";
  display: block;
  height: 1px;
  background: var(--vp-gray-200);
}

/* Nowhere to put an open submenu: the sidebar clips its overflow, so a flyout would be
   cut off. Groups stay shut until the bar is expanded again. */
.pc-sidebar.pc-sidebar-hide .pc-navbar > .pc-item.pc-hasmenu > .pc-submenu {
  display: none;
}

.pc-sidebar.pc-sidebar-hide .sidebar-bottom-panel,
.pc-sidebar.pc-sidebar-hide .subscription-widget {
  display: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pc-sidebar-header {
    padding: 10px 15px;
    min-height: 60px;
  }

  /* The toggle keeps its 36px here, so the logo holds the 1:1 pairing too. */
  .logo-lg {
    max-width: 150px;
  }
}
</style>
